// React
import { useEffect, useState } from "react";
import { Link as ReactRouterLink, useNavigate, useParams } from "react-router-dom";
import React from "react";

// Hooks and Redux
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { refreshMasterData } from "../../../redux/slices/masterDataSlice"

// MUI Components
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';

// MUI Icons
import HomeIcon from '@mui/icons-material/Home';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import PaymentsIcon from '@mui/icons-material/Payments';

// Utils
import { ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import jwtDecode from "jwt-decode";
import Swal from "sweetalert2";

// Styles
import { green } from "@mui/material/colors";

// Types
import { TokenPayload } from "../../../@types";

// Services
import {
    createPayGroup,
    updatePayGroup,
    getPayGroupById,
} from "../../../services/MasterDataService";

interface FormData {
    pay_group_id: string;
    label: string;
    is_active: boolean;
}

type ModeType = "add" | "edit"

interface MyEditorProps {
  type: ModeType
  editId?: string
}

const ManagePayGroup: React.FC<MyEditorProps> = ({ type = "add" }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)

  const dispatch = useAppDispatch()

  const userRoleType = useAppSelector((state) => state.user.roletype)
  const havePermission = userRoleType === "Super Admin"

  const [formData, setFormData] = useState<FormData>({
    pay_group_id: "",
    label: "",
    is_active: true,
  })
  const [dataLoading, setDataLoading] = useState<boolean>(isEdit)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  if (!havePermission) {
    navigate("/forbidden", { replace: true })
  }

  useEffect(() => {
    if (!isEdit || !id) return
    const fetchData = async () => {
      try {
        setDataLoading(true)
        const response = await getPayGroupById(id)
        if (response.data?.success === false) {
          Swal.fire({
            title:
              i18n.language === "th"
                ? response.data?.message_th
                : response.data?.message_en,
            icon: "error",
            confirmButtonColor: "#43A047",
            confirmButtonText: "OK!",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then((result) => {
            if (result.isConfirmed)
              navigate("/admin/masterdata/pay-groups", { replace: true })
          })
          return
        }
        const item = response?.data?.payGroup
        setFormData({
          pay_group_id: item.pay_group_id,
          label: item.label,
          is_active: item.is_active,
        })
      } catch (error) {
        navigate("/servererror", { replace: true })
      } finally {
        setDataLoading(false)
      }
    }
    fetchData()
  }, [id, isEdit, navigate, i18n.language])

  const formValidationSchema = Yup.object().shape({
    ...(type === "edit"
      ? {}
      : {
          pay_group_id: Yup.string().required(
            `${t("Pay Group ID is required")}`
          ),
        }),
    label: Yup.string().required(`${t("Label is required")}`),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(formValidationSchema) })

  const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, is_active: event.target.checked }))
  }

  const onSubmit = (values: any) => {
    const submit = async () => {
      try {
        const parsedToken: TokenPayload = jwtDecode(localStorage.access_token)
        setSubmitLoading(true)
        let response
        if (type === "edit") {
          response = await updatePayGroup(id as string, {
            pay_group_id: formData.pay_group_id,
            label: values.label,
            is_active: formData.is_active,
            modified_by: parsedToken.Username,
          })
        } else {
          response = await createPayGroup({
            pay_group_id: values.pay_group_id,
            label: values.label,
            is_active: formData.is_active,
            created_by: parsedToken.Username,
          })
        }
        if (response) {
          setSubmitLoading(false)
          const responseData = response.data
          if (!responseData?.success) {
            const errorMessage =
              response?.data?.error ||
              response?.data?.error ||
              responseData.message_th ||
              "เกิดข้อผิดพลาด"

            toast.error(errorMessage)
          } else {
            Swal.fire({
              title:
                i18n.language === "th"
                  ? responseData.message_th
                  : responseData.message_en,
              icon: "success",
              confirmButtonColor: "#43A047",
              confirmButtonText: "OK!",
              allowOutsideClick: false,
              allowEscapeKey: false,
            }).then((result) => {
              if (result.isConfirmed) {
                dispatch(refreshMasterData())
                navigate("/admin/masterdata/pay-groups", { replace: true })
              }
            })
          }
        }
      } catch (error) {
        navigate("/servererror", { replace: true })
      }
    }
    submit()
  }

  if (dataLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <ToastContainer style={{ width: "400px" }} />
      {havePermission && (
        <>
          <Box sx={{ height: "auto", maxWidth: "100%", mt: 2 }}>
            <Box display="flex" gap={3} sx={{ mb: 3 }}>
              <Box
                display="flex"
                component={ReactRouterLink}
                onClick={() => window.history.back()}
                sx={{
                  textDecoration: "none",
                  color: "#0d47a1",
                  alignItems: "center",
                  ":hover": { color: "#1976d2" },
                }}
              >
                <ChevronLeftIcon fontSize="small" />
                <Typography sx={{ fontWeight: "bold" }}>{t("Back")}</Typography>
              </Box>
              <Breadcrumbs aria-label="breadcrumb">
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  color="inherit"
                  component={ReactRouterLink}
                  to="/admin"
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {t("Dashboard")}
                </Link>
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  color="inherit"
                  component={ReactRouterLink}
                  to="/admin/masterdata/pay-groups"
                >
                  <PaymentsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {t("Pay Group")}
                </Link>
                <Typography
                  sx={{ display: "flex", alignItems: "center" }}
                  color="text.primary"
                >
                  {type === "edit" ? (
                    <>
                      <EditIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                      {t("Edit Pay Group")}
                    </>
                  ) : (
                    <>
                      <AddCircleOutlineIcon
                        sx={{ mr: 0.5 }}
                        fontSize="inherit"
                      />
                      {t("Create New Pay Group")}
                    </>
                  )}
                </Typography>
              </Breadcrumbs>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "70px",
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", textAlign: "left" }}
            >
              {type === "edit"
                ? t("Edit Pay Group")
                : t("Create New Pay Group")}
            </Typography>
          </Box>

          <Box
            component="form"
            noValidate
            sx={{ mt: 3 }}
            onKeyDown={(e: React.KeyboardEvent) =>
              e.key === "Enter" && e.preventDefault()
            }
          >
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="pay_group_id"
                  label={t("Pay Group ID")}
                  disabled={isEdit}
                  {...(!isEdit ? register("pay_group_id") : {})}
                  error={!!(errors as any).pay_group_id}
                  value={isEdit ? formData.pay_group_id : undefined}
                  onChange={
                    isEdit
                      ? undefined
                      : (event: React.ChangeEvent<HTMLInputElement>) => {
                          setFormData((prev) => ({
                            ...prev,
                            pay_group_id: event.target.value,
                          }))
                          ;(errors as any).pay_group_id = undefined
                        }
                  }
                />
                <Typography
                  variant="inherit"
                  color="error"
                  align="left"
                  pt={1}
                  fontSize={13}
                >
                  {(errors as any).pay_group_id?.message}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="label"
                  label={t("Label")}
                  {...register("label")}
                  error={!!(errors as any).label}
                  value={formData.label}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData((prev) => ({
                      ...prev,
                      label: event.target.value,
                    }))
                    ;(errors as any).label = undefined
                  }}
                />
                <Typography
                  variant="inherit"
                  color="error"
                  align="left"
                  pt={1}
                  fontSize={13}
                >
                  {(errors as any).label?.message}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  label={t("Active")}
                  control={
                    <Checkbox
                      checked={formData.is_active}
                      onChange={handleActiveChange}
                      inputProps={{ "aria-label": "Active" }}
                    />
                  }
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  sx={{ mt: 5, mb: 2, color: "white" }}
                  onClick={handleSubmit(onSubmit)}
                  disabled={submitLoading}
                >
                  {type === "edit" ? t("Save") : t("Create")}
                  {submitLoading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: green[500],
                        position: "absolute",
                        zIndex: 999,
                      }}
                    />
                  )}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ mt: 5, mb: 2 }}
                  component={ReactRouterLink}
                  to="/admin/masterdata/pay-groups"
                >
                  {t("Cancel")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </>
  )
}

export default ManagePayGroup;
